import React, { memo, useMemo } from 'react';
import { DemeterCommodity, DemeterInsurancePositionModel, RunCalculationEngineResponse } from '../../../../../Generated/Raven-Demeter';
import { IChartBarDataSeries, IChartDataSeries } from '../../../../Components/Charts/ChartDefinitions';
import ChartWrapper from '../../../../Components/Charts/ChartWrapper/ChartWrapper';
import StackedBarLineChartRaw from '../../../../Components/Charts/StackedBarLine/StackedBarLineChartRaw';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { MarginCompositeModel, MarginQuarterValue } from '../../RiskDefinitions';
import hedgeMapChartsService from './HedgeMapChartsService';
import styles from './MarginCharts.module.scss';

interface IDairyRiskProtectionHedgeMapChartProps {
    runCalculationEngineResponse: RunCalculationEngineResponse;
    getFarmerMarginCalculatorCompositeRowsModels: MarginCompositeModel[];
    filteredPositions: DemeterInsurancePositionModel[];
    isLoading: boolean;
    displayDecimalPlaces: number;
    endingQuarter?: MarginQuarterValue;
    startingQuarter?: MarginQuarterValue;
}

const outputHedgedVolumeChildVariableName = ['insuranceComponentPrice'];

const DairyRiskProtectionHedgeMapChart: React.FC<IDairyRiskProtectionHedgeMapChartProps> = (props: IDairyRiskProtectionHedgeMapChartProps) => {
    const [translations] = useLanguage();
    const dataSourceTag = useMemo(() => translations.dataSource.StoneXCalculations, [translations]);

    const currency = useMemo(() => props.filteredPositions?.[0]?.currency, [props.filteredPositions]);

    const unitOfMeasure = useMemo(() => props.filteredPositions?.[0]?.unitOfMeasure, [props.filteredPositions]);

    const groupedPositions = useMemo(
        () => hedgeMapChartsService.buildDRPGroupedPositions(props.filteredPositions, props.runCalculationEngineResponse?.results),
        [props.filteredPositions, props.runCalculationEngineResponse],
    );

    const filteredGroupedPositions = useMemo(
        () =>
            groupedPositions.filter((group) => {
                const groupYear = group.periodDate.getFullYear();
                const groupQuarter = Math.floor(group.periodDate.getMonth() / 3) + 1;

                if (props.startingQuarter) {
                    if (groupYear < props.startingQuarter.year) {
                        return false;
                    }
                    if (groupYear === props.startingQuarter.year && groupQuarter < props.startingQuarter.quarter) {
                        return false;
                    }
                }

                if (props.endingQuarter) {
                    if (groupYear > props.endingQuarter.year) {
                        return false;
                    }
                    if (groupYear === props.endingQuarter.year && groupQuarter > props.endingQuarter.quarter) {
                        return false;
                    }
                }

                return true;
            }),
        [groupedPositions, props.startingQuarter, props.endingQuarter],
    );

    const barsSeries = useMemo<IChartBarDataSeries[]>(() => {
        if (filteredGroupedPositions.length === 0) {
            return [];
        }

        return [
            {
                label: translations.risk.margin.text.endorsedQuantity,
                data: filteredGroupedPositions.map((group) => ({
                    value: group.endorsedQuantity,
                    asOfDate: hedgeMapChartsService.getDRPQuarterMidpointDate(group.periodDate),
                    isActualValue: true,
                    customLabel: hedgeMapChartsService.formatDRPInsuranceQuarterLabel(group.periodKey),
                    ...(group.productionQuantity > 0 && { percentValue: group.endorsedPercent }),
                })),
                stack: 'endorsed',
            },
            {
                label: translations.risk.margin.text.protectedQuantity,
                data: filteredGroupedPositions.map((group) => ({
                    value: group.protectedQuantity,
                    asOfDate: hedgeMapChartsService.getDRPQuarterMidpointDate(group.periodDate),
                    isActualValue: true,
                    customLabel: hedgeMapChartsService.formatDRPInsuranceQuarterLabel(group.periodKey),
                    ...(group.productionQuantity > 0 && { percentValue: group.protectedPercent }),
                })),
                stack: 'protected',
            },
        ];
    }, [filteredGroupedPositions]);

    const linesSeries = useMemo<IChartDataSeries[]>(() => {
        if (filteredGroupedPositions.length === 0 || !props.filteredPositions?.length || !props.runCalculationEngineResponse?.results) {
            return [];
        }

        const uniqueCommodities = hedgeMapChartsService.getUniqueCommoditiesFromPositions(props.filteredPositions);
        const lines: IChartDataSeries[] = [];

        const rangeStartDate = filteredGroupedPositions[0].periodDate;
        const lastGroup = filteredGroupedPositions[filteredGroupedPositions.length - 1];
        const rangeEndDate = new Date(lastGroup.periodDate.getFullYear(), lastGroup.periodDate.getMonth() + 3, 0);

        const compositeRowsInRange = props.getFarmerMarginCalculatorCompositeRowsModels.filter((model) => {
            const modelDate = new Date(model.asOfDate);
            return modelDate >= rangeStartDate && modelDate <= rangeEndDate;
        });

        uniqueCommodities.forEach((commodity) => {
            const futuresVariableName = hedgeMapChartsService.getFuturesVariableNameForCommodity(commodity);

            if (futuresVariableName) {
                const monthlyFuturesPrices = hedgeMapChartsService
                    .mapCompositeModelsToSeriesData(props.runCalculationEngineResponse?.results, compositeRowsInRange, futuresVariableName)
                    .filter((point) => point.value > 0);

                if (monthlyFuturesPrices.some((point) => point.value > 0)) {
                    lines.push({
                        label: `${translations.commodity[commodity as DemeterCommodity]} - ${translations.risk.margin.text.futuresPrice}`,
                        data: monthlyFuturesPrices,
                    });
                }
            }

            const insuranceVariableName = hedgeMapChartsService.getInsuranceVariableNameForCommodity(commodity);

            if (insuranceVariableName) {
                const monthlyInsurancePrices = hedgeMapChartsService
                    .mapCompositeModelsToSeriesData(props.runCalculationEngineResponse?.results, compositeRowsInRange, insuranceVariableName)
                    .filter((point) => point.value > 0);

                if (monthlyInsurancePrices.some((point) => point.value > 0)) {
                    lines.push({
                        label: `${translations.commodity[commodity as DemeterCommodity]} - ${translations.risk.margin.text.weightedAverageCoveragePrice}`,
                        data: monthlyInsurancePrices,
                    });
                }
            }
        });

        const outputHedgedVolumeChildren = props.getFarmerMarginCalculatorCompositeRowsModels[0]?.outputHedgedVolume?.children ?? [];

        outputHedgedVolumeChildren
            .filter((child) => outputHedgedVolumeChildVariableName.includes(child.variableName))
            .forEach((child) => {
                const seriesData = hedgeMapChartsService
                    .mapCompositeModelsToSeriesData(props.runCalculationEngineResponse?.results, compositeRowsInRange, child.variableName)
                    .filter((point) => point.value > 0);

                if (seriesData.some((point) => point.value > 0)) {
                    lines.push({
                        label: child.displayName,
                        data: seriesData,
                    });
                }
            });

        return lines;
    }, [filteredGroupedPositions, props.filteredPositions, props.runCalculationEngineResponse, props.getFarmerMarginCalculatorCompositeRowsModels]);

    const isChartDataReady = useMemo(
        () => filteredGroupedPositions.length > 0 && barsSeries.length > 0 && linesSeries.length > 0,
        [filteredGroupedPositions, barsSeries, linesSeries],
    );

    return (
        <div className={styles.margin_chart}>
            <ChartWrapper
                name="DairyRiskProtectionHedgeMapChart"
                title={translations.risk.headers.marginHedgeMap}
                dataSourceTag={dataSourceTag}
                isLoading={props.isLoading || !isChartDataReady}
                track={{ eventCategory: 'MarginCalculator', eventAction: 'DairyRiskProtectionHedgeMapChart' }}
            >
                <StackedBarLineChartRaw
                    barsSeries={barsSeries}
                    linesSeries={linesSeries}
                    unitOfMeasure={unitOfMeasure}
                    currency={currency}
                    displayDecimalPlacesMaximum={props.displayDecimalPlaces}
                    displayDecimalPlacesMinimum={props.displayDecimalPlaces}
                    useVerticalSeparation
                    showLineMarkers
                />
            </ChartWrapper>
        </div>
    );
};

export default memo(DairyRiskProtectionHedgeMapChart);
